/*
 * Authors: mariushirjanu, Koen
 * Created: 10/03/2015
 * Upgraded: 21/02/2023
 */
import * as _ from 'underscore';
import { Subscription } from 'rxjs';
import { Component, Input, Output, OnInit, OnChanges, OnDestroy, SimpleChanges, EventEmitter, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { ICartItemStatus, ICatalogCartItem, PriceCartService } from './price-cart.service';
import { PartConfiguratorService } from './part-configurator.service';
import { TranslationService } from '../../../shared/translation/translation.service';
import { ViewEncapsulation } from '@angular/core';
import { GaEventService, IGAEvent } from '../../../shared/services/ga-event.service';
import { EVENT_ORDER_CART_STATUS_UPDATED } from '../../../shared/app-events';

@Component({
  selector: 'add-to-cart',
  template: `
    <div class="add-to-cart" #addToCart>
      <label for="quantity" class="d-none">Quantity</label>
      <span class="count">
        <button class="btn down" (click)="dec()" title="Decrease quantity" [disabled]="status.busy || arrowBtnDisabled">
          <i class="fa fa-caret-down"></i>
        </button>
        <span *ngIf="syncedInput">
          <input type="number" min="0" step="1" size="4" id="quantityCart" class="form-control" [(ngModel)]="status.count" [disabled]="status.busy || status.inputDisabled" [ngClass]="{ hidden: noStatus }" (blur)="onBlur()" (keyup)="onKeyUp($event)"/>
        </span>
        <span *ngIf="!syncedInput && !status.countCartItem">
          <input type="number" min="0" step="1" size="4" id="quantityCatalog" class="form-control" [(ngModel)]="catalogCounter" [disabled]="status.busy || status.inputDisabled" [ngClass]="{ hidden: noStatus }" (blur)="onBlur()" (keyup)="onKeyUp($event)"/>
        </span>
        <span *ngIf="!syncedInput && status.countCartItem">
          <input type="number" size="4" id="totalQuantityCatalog" class="form-control" [ngModel]="status.countCartItem" [disabled]="true" [ngClass]="{ hidden: noStatus }" title="For this item the quantity can be changed from the cart"/>
        </span>
        
        <button class="btn up" (click)="inc()" title="Increase quantity" [disabled]="status.busy || arrowBtnDisabled">
          <i class="fa fa-caret-up"></i>
        </button>
      </span>
      <button [busyButton]="noStatus ? false : status.busy || busyButton" [bbPreventDisable]="true" [bbReplaceIcon]="true" [bbIconMargin]="!mini" class="btn add" [ngClass]="btnType" [disabled]="disabled" (click)="addIt()" [title]="buttonText">
        <i class="fa" [ngClass]="icon"></i>
        <span class="button-text"> {{buttonText}}</span>
      </button>
    </div>
  `,
  styleUrls: ['cart.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AddToCartComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('addToCart', { static: true }) public addToCart:ElementRef<HTMLFormElement>;
  @Input() public product:ICatalogCartItem;
  @Input() public catalogView:boolean; // true if the component is loaded on the catalog view
  @Input() public noStatus:boolean; // true - means to hide the input element for quantity (used only for section compatibility items)
  @Input() public mini:boolean;
  @Output() public cartAction:EventEmitter<{product:ICatalogCartItem; count:number}> = new EventEmitter<{product:ICatalogCartItem; count:number}>(); // if you want to override priceCartService.addToCart

  public syncedInput:boolean;
  public status:ICartItemStatus;
  public arrowBtnDisabled:boolean;
  public catalogCounter:number;
  public buttonText:string;
  public busyButton:boolean;
  public icon:string;
  public btnType:string;
  public disabled:boolean;
  private eventHandler:Subscription;

  constructor(
    private priceCartService:PriceCartService,
    private partConfiguratorService:PartConfiguratorService,
    private translationService:TranslationService,
    private eventService:GaEventService
  ) {
    /***/
  }

  public ngOnInit():void {
    this.listen();
  }

  private listen():void {
    this.eventHandler = this.eventService.events.subscribe((event:IGAEvent) => {
      if (event.name === EVENT_ORDER_CART_STATUS_UPDATED) {
        this.updateButton();
      }
    });
  }

  public ngOnChanges(changes:SimpleChanges):void {
    if(changes.product){
      this.product = changes.product.currentValue;
    }

    if(changes.catalogView){
      this.catalogView = changes.catalogView.currentValue;
    }

    if(changes.noStatus){
      this.noStatus = changes.noStatus.currentValue;
    }

    // boolean flag that shows if the input quantity is synced between the catalog view and cart view
    // true: the input is updating in both views (cart and view), false: the input is updating individually (cart or view)
    this.syncedInput = this.catalogView? !this.product.partConfigurationExist : true;
    this.updateButton();
  }

  public ngAfterViewInit():void {
    setTimeout(() =>  this.updateButton());
  }

  public updateButton(fetchStatus:boolean = true):void {
    if(fetchStatus){
      this.status = this.priceCartService.getItemStatus(this.product, this.catalogView) || <any>{};
    }

    const orderLineStatus:string = this.status.product?.orderLineStatus;
    const inValid:boolean = this.status.product?.inValid;
    const count:number = this.status.count;
    const cartCount:number = this.status.cartCount;
    const countCartItem:number = this.status.countCartItem;
    const element:JQuery = $(this.addToCart.nativeElement);

    if (this.mini) {
      element.find('.btn.down').addClass('d-none');
      element.find('.btn.up').addClass('d-none');
      element.find('.button-text').addClass('d-none');

      this.inputElm.addClass('input-sm');
      element.find('.btn.add').addClass('btn-sm');
    }

    if (orderLineStatus === 'NA' || inValid) {
      this.buttonText = this.translationService.instant('CATALOG.DETAIL.INVALID');
      this.icon = 'fa-warning';
      this.btnType = 'btn-primary';
      this.disabled = true;
      this.status.inputDisabled = true;
      this.arrowBtnDisabled = true;
    } else if (count !== cartCount) {
      this.buttonText = this.translationService.instant('CATALOG.DETAIL.UPDATE_CART');
      this.icon = 'fa-shopping-cart';
      this.btnType = 'btn-primary';
      this.disabled = false;
      this.arrowBtnDisabled = false;
    } else if ((!cartCount || !this.syncedInput) && !countCartItem) {
      this.buttonText = this.translationService.instant('CATALOG.DETAIL.ADD_TO_CART');
      this.icon = 'fa-shopping-cart';
      this.btnType = 'btn-primary';
      this.disabled = false;
      this.arrowBtnDisabled = false;
    } else if ((!cartCount || !this.syncedInput) && countCartItem) {
      this.buttonText = this.translationService.instant('CATALOG.DETAIL.ADD_TO_CART');
      this.icon = 'fa-check';
      this.btnType = 'btn-success';
      this.disabled = false;
      this.arrowBtnDisabled = true;
    } else {
      this.buttonText = this.translationService.instant('CATALOG.DETAIL.ADDED_TO_CART');
      this.icon = 'fa-check';
      this.btnType = 'btn-success';
      this.disabled = true;
      this.arrowBtnDisabled = false;
    }
  }

  public dec = ():void => {
    if (!this.status.count) {
      this.status.count = 0;
    } else {
      this.status.count--;
    }

    this.addIt();
  };

  public inc = (noAdd?:boolean):void => {
    if (!this.status.count) {
      this.status.count = 0;
    }
    this.status.count++;

    if(!noAdd) {
      this.addIt();
    }
  };

  public onBlur():void {
    if (this.status.count !== this.status.cartCount) {
      this.addIt();
    }
  }

  public onKeyUp(event:KeyboardEvent):void {
    if (event.key === 'Enter') {
      this.inputElm.blur();
    }
    this.updateButton(false);
  }

  public addIt():void {
    // prevent adding the item to the cart when quantity is a non-numeric value
    if (!this.inputElm.val().match(/^[0-9]*$/)) {
      return;
    }

    let addToCart:Function = this.priceCartService.addToCart;
    if (this.cartAction.observers.length) {
      addToCart = (product:ICatalogCartItem, count:number) => {
        return this.cartAction.emit({ product, count });
      };
    }

    if (!this.syncedInput) {
      if (!this.catalogCounter) {
        this.catalogCounter = 1;
      }
      this.busyButton = true;
    }

    let quantity:number = Number(this.catalogCounter || this.status.count);
    const product:ICatalogCartItem = _.clone(this.product);

    const updateCart = ():void => {
      if (this.noStatus) {
        this.inc(true); // only increment the counter (without re-adding the product as it might result a loop)
      }

      // check if an item with the same part suffix exists already in the cart, if it does -> increment item quantity when adding it from catalog
      if (!this.syncedInput && product.partSuffix) {
        const cartItemWithSuffix:ICartItemStatus = this.priceCartService.getItemStatus(product);
        if (cartItemWithSuffix) {
          quantity += cartItemWithSuffix.count;
        }
      }

      addToCart(product, quantity);
    };

    const afterCartUpdate = ():void => {
      this.catalogCounter = undefined;
      this.busyButton = false;

      setTimeout(() => {
        this.updateButton();
      });
    };

    if (this.catalogView) {
      this.partConfiguratorService.selectPart(product, quantity)
        .then(updateCart)
        .finally(afterCartUpdate);
    } else {
      // do not ask for part configurator when updating the quantity from the cart, instead the part configurator can be changed when clicking on edit icon
      updateCart();
      afterCartUpdate();
    }
  }

  private get inputElm():JQuery {
    return $(this.addToCart.nativeElement).find('input');
  }

  public ngOnDestroy():void {
    this.eventHandler.unsubscribe();
  }
}